import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveWeeklySummary, DEFAULT_META } from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import { rememberAuthoritative } from '../data/weeklySummaryOverrides.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useSaveWeeklySummary — save one goal's weekly summary via the
 * server-side partial-update endpoint.
 *
 * Input: { goalKey, patch? | entry? }
 *   patch  (preferred): object with only the field(s) being changed.
 *          Server merges onto the current KV state — safe against
 *          concurrent writers on other fields.
 *   entry  (legacy/escape hatch): full { tldr, wins, flags, ... }.
 *          Use only when explicitly overwriting the whole entry
 *          (e.g. carry-forward populating an empty slot). Not safe
 *          against concurrent writers because stale draft values for
 *          untouched fields get sent over the wire.
 *
 * Flow:
 *   1. onMutate   → optimistic cache write (merge patch/entry into
 *                   existing entry locally) + undo snapshot
 *   2. mutationFn → POST /meta/weeklySummary
 *   3. onSuccess  → reconcile cache with the server's authoritative
 *                   merged entry
 *   4. onError    → rollback optimistic write
 */
export function useSaveWeeklySummary() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ goalKey, entry, patch }) => {
      const savedEntry = await saveWeeklySummary({ goalKey, entry, patch })
      return { goalKey, entry: savedEntry }
    },

    onMutate: async ({ goalKey, entry, patch }) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      useHistoryStore
        .getState()
        .pushSnapshot(
          'Weekly summary',
          queryClient.getQueryData(['goals']),
          previous
        )

      // Apply the optimistic update locally. For patches, merge on
      // top of existing entry; for full entries, replace.
      const existingEntry = previous.weeklySummary?.[goalKey] || {}
      const optimisticEntry = patch
        ? { ...existingEntry, ...patch }
        : entry
      queryClient.setQueryData(['meta'], {
        ...previous,
        weeklySummary: {
          ...(previous.weeklySummary || {}),
          [goalKey]: optimisticEntry,
        },
      })

      return { previous }
    },

    onSuccess: ({ goalKey, entry }) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], {
        ...current,
        weeklySummary: {
          ...(current.weeklySummary || {}),
          [goalKey]: entry,
        },
      })
      // Guard against stale /meta refetches (other clients' Ably
      // events landing within the KV propagation window) resurrecting
      // content we just cleared.
      rememberAuthoritative(goalKey, entry)
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
  return withArchiveGuard(mutation)
}
